import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PayGatePaymentModuleSpecsFragment } from '../../../../api/fragments/PayGatePaymentModuleSpecs';
import {
    type DeletePayGatePaymentSettingsMutation,
    type DeletePayGatePaymentSettingsMutationVariables,
    DeletePayGatePaymentSettingsDocument,
} from '../../../../api/mutations/deletePayGatePaymentSettings';

export type PayGatePaymentSettingActionsProps = {
    module: PayGatePaymentModuleSpecsFragment;
    paymentSetting: PayGatePaymentModuleSpecsFragment['paymentSettings'][number];
};

const PayGatePaymentSettingActions = ({ module, paymentSetting }: PayGatePaymentSettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = paymentSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['paygatePaymentModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.content'),
            okText: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeletePayGatePaymentSettingsMutation, DeletePayGatePaymentSettingsMutationVariables>({
                        mutation: DeletePayGatePaymentSettingsDocument,
                        variables: { moduleId, id: paymentSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('paygatePaymentModuleDetails:paymentSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/paygatePayment/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('paygatePaymentModuleDetails:paymentSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('paygatePaymentModuleDetails:paymentSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default PayGatePaymentSettingActions;
